import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, Plus, MousePointerClick, ChartBar as BarChart2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MobileSubMenuHeader } from '@/components/layout';

interface FormsProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const Forms: React.FC<FormsProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const [mobileSearchQuery, setMobileSearchQuery] = React.useState('');

  const handleMobileMenuToggle = () => {
    if (onMobileSubMenuToggle) {
      onMobileSubMenuToggle(null);
    }
  };

  const handleMobileAdd = () => {
    alert('Create new form (coming soon)');
  };

  return (
    <div className={`min-h-screen ${isGlobalDarkMode ? 'bg-background' : 'bg-transparent'}`}>
      <Helmet>
        <title>Forms | AgencyPlus</title>
        <meta name="description" content="Create and manage forms to capture leads and feedback" />
        <link rel="canonical" href="/forms" />
      </Helmet>
      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={handleMobileAdd}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search forms..."
          showSearch={true}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`max-w-7xl mx-auto p-8 ${isMobile ? 'pt-20' : '' }`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2 text-foreground">Forms</h1>
            <p className="text-lg text-muted-foreground">
              Create and manage forms to capture leads and feedback
            </p>
          </div>
          <Button className="bg-workspace-accent hover:bg-workspace-accent-hover">
            <Plus className="w-4 h-4 mr-2" />
            New Form
          </Button>
        </div>

        <div className="rounded-2xl p-12 text-center mb-8 bg-card border border-border">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-workspace-accent/15">
            <FileText className="w-10 h-10 text-workspace-accent" />
          </div>

          <h2 className="text-3xl font-bold mb-4 text-foreground">Coming Soon</h2>
          <p className="text-lg max-w-2xl mx-auto mb-8 text-muted-foreground">
            We're creating a powerful form builder that lets you design custom forms, collect
            responses, and integrate seamlessly with your CRM workflows.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="bg-muted border-border">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center bg-violet-500/15">
                  <MousePointerClick className="w-6 h-6 text-violet-500" />
                </div>
                <CardTitle className="text-lg text-foreground">
                  Interactive Builder
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-muted-foreground">
                  Create forms with multiple field types, conditional logic, and validation
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-muted border-border">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center bg-green-500/15">
                  <Share2 className="w-6 h-6 text-green-500" />
                </div>
                <CardTitle className="text-lg text-foreground">
                  Easy Sharing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-muted-foreground">
                  Embed forms on your website or share via direct links
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-muted border-border">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center bg-orange-500/15">
                  <BarChart2 className="w-6 h-6 text-orange-500" />
                </div>
                <CardTitle className="text-lg text-foreground">
                  Response Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-muted-foreground">
                  Analyze form entries with detailed reports and insights
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forms;
